/**
 * SCROLL STATE MANAGER UTILITIES
 *
 * Centralized scroll state management utility providing body CSS classes for
 * scroll-dependent UI behaviors, such as sticky headers and banner visibility.
 *
 * The manager's output is body classes plus the `getState()` accessor. It
 * dispatched three `scroll-state:*` events historically; none were ever consumed,
 * so they were removed rather than left as an unused surface.
 *
 * **BODY CLASSES MANAGED:**
 * - `body--scrolled`: Applied when page is scrolled away from top (scrollY > 1px) and cleared only after scroll ends at the top - **ACTIVELY USED**
 * - `body--scrolling`: Applied during active scrolling, removed when scroll stops - **Available for future use**
 * - `body--scrolling-up`: Applied when scrolling upward, removed when scroll stops - **Available for future use**
 * - `body--scrolling-down`: Applied when scrolling downward, removed when scroll stops - **Available for future use**
 *
 * Key Features:
 * - Passive scroll event listeners for optimal performance
 * - Debounced scroll-end detection (150ms delay)
 * - Direction change threshold (5px) to prevent flickering
 * - Scrolled state hysteresis (on at 1px, off at 0px) with unlock delay (250ms) to prevent jitter near top
 * - Body classes for CSS selectors
 * - Memory-efficient with proper cleanup
 *
 * Integration Patterns:
 * - CSS selectors: Use body classes for scroll-dependent styles
 * - Global access: Available as `window.scrollStateManager` and `window.theme.utilities.scrollStateManager`
 * - Auto-initialization: Automatically initializes on script load
 *
 * @example
 * // CSS: Header positioning when scrolled
 * body.body--scrolled #site-header {
 *   grid-template-rows: 0fr;
 *   position: fixed;
 * }
 *
 * // CSS: Banner visibility
 * body:not(.body--scrolled) .app-banner {
 *   display: block;
 * }
 *
 * // Check current state programmatically
 * const state = window.scrollStateManager.getState();
 * // Returns: { scrollY, isScrolling, isScrolled, isAtTop, direction }
 *
 * @namespace ScrollStateManagerUtilities
 * @since 2025
 * @version 2.0.0
 */

/**
 * Scroll State Manager Class
 * Manages scroll-related body classes for UI state management
 * @class ScrollStateManager
 */
export class ScrollStateManager {
  constructor() {
    /** @type {number} Last recorded scroll position in pixels */
    this.lastScrollY = window.pageYOffset;

    /** @type {number|null} Timeout ID for scroll end detection */
    this.scrollTimeout = null;

    /** @type {number} Minimum pixel difference to detect direction change */
    this.scrollThreshold = 5;

    /** @type {number} Scroll position to consider page "scrolled" (hysteresis ON threshold, used as fallback when no banner height is available) */
    this.scrolledThresholdOn = 1;
    /** @type {number} Scroll position to consider page "at top" (hysteresis OFF threshold) */
    this.scrolledThresholdOff = 0;

    /** @type {number} Milliseconds to wait before removing scrolling classes */
    this.scrollEndDelay = 150;

    /** @type {boolean} Whether a scroll event is currently active */
    this.isScrolling = false;

    /** @type {boolean} Current scrolled state */
    this.isScrolled = false;

    /** @type {string|null} Current scroll direction */
    this.direction = null;
    /** @type {string|null} Last known scroll direction */
    this.lastDirection = null;
    /** @type {number} Timestamp until which scrolled state should remain locked */
    this.scrolledLockUntil = 0;
    /** @type {number} Minimum ms to keep scrolled state after activating */
    this.scrolledUnlockDelay = 250;

    /** @type {Function} Bound scroll handler for cleanup */
    this._boundHandleScroll = null;

    this.init();
  }

  /**
   * Initialize the scroll state manager
   * Sets up initial state and event listeners
   */
  init() {
    // Set initial state
    this.updateScrolledState();

    // Bind handler and store reference for cleanup
    this._boundHandleScroll = this.handleScroll.bind(this);

    // Listen to scroll events with passive flag for better performance
    window.addEventListener('scroll', this._boundHandleScroll, { passive: true });
  }

  /**
   * Handle scroll events
   * Updates scroll state classes and manages direction tracking
   * @private
   */
  handleScroll() {
    const currentScrollY = window.pageYOffset;

    // Add scrolling class when scroll begins
    if (!this.isScrolling) {
      document.body.classList.add('body--scrolling');
      this.isScrolling = true;
    }

    // Clear existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Update direction classes
    // Both update body classes as a side effect, which is the manager's output.
    this.updateScrollDirection(currentScrollY);
    this.updateScrolledState();

    // Set timeout to remove scrolling classes when scroll stops
    this.scrollTimeout = setTimeout(() => {
      document.body.classList.remove('body--scrolling');
      document.body.classList.remove('body--scrolling-up');
      document.body.classList.remove('body--scrolling-down');
      this.isScrolling = false;
      this.direction = null;

      // Re-evaluate scrolled state once scrolling has stopped
      this.updateScrolledState();
    }, this.scrollEndDelay);

    // Update last scroll position
    this.lastScrollY = currentScrollY;
  }

  /**
   * Update scroll direction classes based on current position
   * @param {number} currentScrollY - Current scroll position in pixels
   * @returns {boolean} Whether direction changed
   * @private
   */
  updateScrollDirection(currentScrollY) {
    const scrollDifference = currentScrollY - this.lastScrollY;

    // Only update direction if scroll difference exceeds threshold
    // This prevents flickering on small scroll movements
    if (Math.abs(scrollDifference) < this.scrollThreshold) {
      return false;
    }

    const newDirection = scrollDifference > 0 ? 'down' : 'up';
    const directionChanged = this.direction !== newDirection;

    if (directionChanged) {
      this.direction = newDirection;
      this.lastDirection = newDirection;

      if (newDirection === 'down') {
        document.body.classList.remove('body--scrolling-up');
        document.body.classList.add('body--scrolling-down');
      } else {
        document.body.classList.remove('body--scrolling-down');
        document.body.classList.add('body--scrolling-up');
      }
    }

    return directionChanged;
  }

  /**
   * Update the scrolled state class based on vertical position
   * Adds body--scrolled when scrolled past threshold with hysteresis, using app banner height when available
   * @returns {boolean} Whether scrolled state changed
   * @private
   */
  updateScrolledState() {
    const currentScrollY = window.pageYOffset;
    const wasScrolled = this.isScrolled;
    const bannerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--app-banner-height')) || 0;
    const dynamicThresholdOn = bannerHeight > 0 ? bannerHeight : this.scrolledThresholdOn;
    // Use hysteresis to prevent flicker/jitter near top when layout shifts occur
    // - Turn ON when we pass scrolledThresholdOn
    // - Turn OFF only when we return to scrolledThresholdOff (top)
    // - Do NOT turn off while actively scrolling (prevents oscillation during layout shifts)
    if (this.isScrolled) {
      if (!this.isScrolling) {
        if (currentScrollY <= this.scrolledThresholdOff) {
          const now = performance.now();
          const lockActive = now < this.scrolledLockUntil;
          this.isScrolled = lockActive ? this.isScrolled : false;
        } else {
          this.isScrolled = true;
        }
      }
    } else {
      this.isScrolled = currentScrollY > dynamicThresholdOn;
    }

    if (wasScrolled !== this.isScrolled) {
      if (this.isScrolled) {
        this.scrolledLockUntil = performance.now() + this.scrolledUnlockDelay;
        document.body.classList.add('body--scrolled');
      } else {
        this.scrolledLockUntil = 0;
        document.body.classList.remove('body--scrolled');
      }
      return true;
    }

    return false;
  }

  /**
   * Get current scroll state information
   * @returns {Object} Current scroll state
   */
  getState() {
    return {
      scrollY: window.pageYOffset,
      isScrolling: this.isScrolling,
      isScrolled: this.isScrolled,
      isAtTop: !this.isScrolled,
      direction: this.direction
    };
  }

  /**
   * Cleanup method to remove event listeners
   * Called when the manager needs to be destroyed
   */
  destroy() {
    // Clear scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }

    // Remove scroll event listener
    if (this._boundHandleScroll) {
      window.removeEventListener('scroll', this._boundHandleScroll);
      this._boundHandleScroll = null;
    }

    // Remove all managed classes
    document.body.classList.remove(
      'body--scrolling',
      'body--scrolling-up',
      'body--scrolling-down',
      'body--scrolled'
    );

    // Reset state
    this.isScrolling = false;
    this.isScrolled = false;
      this.direction = null;
    this.lastScrollY = 0;
  }
}

/**
 * Auto-initialization
 * Automatically creates and configures the global scroll state manager instance
 * Sets up global references for easy access throughout the theme
 */
(() => {
  // Create and initialize the scroll state manager
  const scrollStateManager = new ScrollStateManager();

  // Make available globally for access from any script
  window.scrollStateManager = scrollStateManager;

  // Also attach to theme utilities for consistency
  if (!window.theme) {
    window.theme = {};
  }
  if (!window.theme.utilities) {
    window.theme.utilities = {};
  }
  window.theme.utilities.scrollStateManager = scrollStateManager;
})();
